import { useCallback } from 'react';
import useTimeout from '@restart/hooks/useTimeout';

/**
 * Adapted from: https://github.com/react-restart/hooks/blob/master/src/useDebouncedCallb
 * MIT Copyright (c) 2018 Jason Quense
 *
 * Creates a debounced async function that will invoke the input function after the
 * specified delay.
 *
 * @param fn a function that will be debounced
 * @param delay The milliseconds delay before invoking the function
 */
const useDebouncedAsyncCallback = <ReturnValue, TCallback extends (...args: any[]) => Promise<ReturnValue>>(
  fn: TCallback,
  delay: number,
): ((...args: Parameters<TCallback>) => Promise<ReturnValue>) => {
  const timeout = useTimeout();

  return useCallback(
    (...args: any[]) => {
      return new Promise<ReturnValue>((resolve) => {
        timeout.set(() => {
          fn(...args).then(resolve);
        }, delay);
      });
    },
    [fn, delay],
  );
};

export default useDebouncedAsyncCallback;
